import React from 'react';
import styled from 'styled-components';
import { AuditChange } from '../../models/audit';

const OPERATION_LABELS: Record<AuditChange['operation'], string> = {
  create: 'Created',
  update: 'Updated',
  delete: 'Deleted',
};

// JSON stored in text columns (widget features, flows) is pretty-printed so diffs are readable.
export const formatValue = (value: unknown): string => {
  if (value === null || value === undefined) return '—';
  if (typeof value === 'string') {
    const trimmed = value.trim();
    if (trimmed.startsWith('{') || trimmed.startsWith('[')) {
      try {
        return JSON.stringify(JSON.parse(trimmed), null, 2);
      } catch {
        return value;
      }
    }
    return value === '' ? '(empty)' : value;
  }
  return JSON.stringify(value, null, 2);
};

const isLong = (text: string) => text.length > 80 || text.includes('\n');

export const changedFieldsOf = (change: AuditChange) => (
  change.operation === 'update'
    ? change.changed_fields
    : Object.keys(change.before || change.after || {})
);

const ChangeDiff = ({ change }: { change: AuditChange }) => {
  const fields = changedFieldsOf(change);
  return (
    <Wrapper>
      <Title>
        <Operation $operation={change.operation}>{OPERATION_LABELS[change.operation]}</Operation>
        <code>{change.table}</code> row <code>{JSON.stringify(change.row_pk)}</code>
        <Model>{change.model} · {change.db_alias}</Model>
      </Title>
      <Fields>
        {fields.map((field) => {
          const before = formatValue(change.before?.[field]);
          const after = formatValue(change.after?.[field]);
          const long = isLong(before) || isLong(after);
          return (
            <Field key={field}>
              <FieldName>{field}</FieldName>
              <Values $long={long}>
                <Value aria-label={`${field} before`} $kind='before' $empty={!change.before}>{change.before ? before : '—'}</Value>
                <Value aria-label={`${field} after`} $kind='after' $empty={!change.after}>{change.after ? after : '—'}</Value>
              </Values>
            </Field>
          );
        })}
      </Fields>
    </Wrapper>
  );
};

export default ChangeDiff;

const Wrapper = styled.div`
  border: 1px solid #e1e3e5;
  border-radius: 6px;
  padding: 12px;
  min-width: 0;
`;

const Title = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 6px;
  margin-bottom: 8px;
  code { font-size: 12px; background: #f4f6f8; padding: 1px 4px; border-radius: 3px; }
`;

const Operation = styled.span<{ $operation: AuditChange['operation'] }>`
  font-weight: 600;
  color: ${({ $operation }) => ($operation === 'delete' ? '#d72c0d' : $operation === 'create' ? '#007f5f' : '#2c6ecb')};
`;

const Model = styled.span`
  color: #6d7175;
  font-size: 12px;
  margin-left: auto;
`;

const Fields = styled.div`
  display: grid;
  gap: 8px;
`;

const Field = styled.div`
  display: grid;
  grid-template-columns: minmax(120px, 180px) minmax(0, 1fr);
  gap: 8px;
  align-items: start;
  @media (max-width: 767px) { grid-template-columns: minmax(0, 1fr); }
`;

const FieldName = styled.span`
  font-family: monospace;
  font-size: 12px;
  color: #44474a;
  overflow-wrap: anywhere;
`;

const Values = styled.div<{ $long: boolean }>`
  display: grid;
  grid-template-columns: ${({ $long }) => ($long ? 'repeat(auto-fit, minmax(240px, 1fr))' : 'repeat(2, minmax(0, 1fr))')};
  gap: 8px;
  min-width: 0;
`;

const Value = styled.pre<{ $kind: 'before' | 'after'; $empty: boolean }>`
  margin: 0;
  padding: 6px 8px;
  border-radius: 4px;
  font-size: 12px;
  white-space: pre-wrap;
  overflow-wrap: anywhere;
  max-height: 320px;
  overflow: auto;
  background: ${({ $kind, $empty }) => ($empty ? '#f6f6f7' : $kind === 'before' ? '#fff4f4' : '#f1f8f5')};
  color: ${({ $empty }) => ($empty ? '#8c9196' : 'inherit')};
`;
